import React, { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import {
  Sidebar,
  sidebarConfig,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import GroupIcon from '@material-ui/icons/Group';
import BuildOutlinedIcon from '@material-ui/icons/BuildOutlined';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import AIIcon from '@material-ui/icons/DeveloperBoard';
import WorkflowIcon from '@material-ui/icons/Extension';
import DataUsageIcon from '@material-ui/icons/DataUsage';
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import BusinessOutlinedIcon from '@material-ui/icons/BusinessOutlined';
import Payments from '@material-ui/icons/PaymentSharp';
import SyncAltIcon from '@material-ui/icons/SyncAlt';
import PolicyIcon from '@material-ui/icons/Policy';
import AdminIcon from '@material-ui/icons/LockOutlined';
import { FeatureFlagged } from '@backstage/core-app-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { BACKOFFICE_PERMISSIONS } from '@internal/backoffice-auth-common';
import { RegisterBackofficeUser } from './RegisterBackofficeUser';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const adminAccess = usePermission({
    permission: BACKOFFICE_PERMISSIONS.manage_backoffice_users,
  });

  return (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      {/* <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />*/}
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        {/* <SidebarItem icon={HomeIcon} to="/" text="Home" />
        <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        <SidebarItem icon={AddCircleOutlineIcon} to="create" text="Create" /> */}
        {/* End global nav */}

        <SidebarScrollWrapper>
          {/* The Insights page is deliberately absent here while it is in
              testing. Its route is still registered, so it is reachable at
              /insights for anyone given the link. */}
          <SidebarItem
            icon={BusinessOutlinedIcon}
            to="organisations"
            text="Organisations"
          />
          <SidebarItem icon={GroupIcon} to="users" text="Users" />
          <SidebarItem
            icon={PlaylistAddCheckIcon}
            to="jobs"
            text="Explore Jobs"
          />
          <SidebarItem icon={DataUsageIcon} to="billing" text="Usage Reports" />
          <SidebarItem
            icon={CloudDownloadIcon}
            to="tatreports"
            text="Gate Artifacts"
          />
          <SidebarItem
            icon={BuildOutlinedIcon}
            to="installations"
            text="Gate Configs"
          />
          <SidebarItem icon={PolicyIcon} to="policy" text="Policies" />
          <SidebarItem icon={DeviceHubIcon} to="routing" text="Routing" />
          <FeatureFlagged with="engines-next">
            <SidebarItem icon={AIIcon} to="engines-next" text="Engine Fleet" />
          </FeatureFlagged>
          <FeatureFlagged without="engines-next">
            <SidebarItem icon={AIIcon} to="engines" text="AI Engines" />
          </FeatureFlagged>
          <SidebarItem icon={WorkflowIcon} to="workflows" text="AI Workflows" />
          <SidebarItem icon={Payments} to="costs" text="Costs" />
          <FeatureFlagged with="jira-sync">
            <SidebarItem
              icon={SyncAltIcon}
              to="customer-state-sync-tester"
              text="JIRA Sync"
            />
          </FeatureFlagged>
          <FeatureFlagged with="admin-page">
            {adminAccess.allowed && (
              <SidebarItem icon={AdminIcon} to="admin" text="Admin" />
            )}
          </FeatureFlagged>
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    <RegisterBackofficeUser />
    {children}
  </SidebarPage>
  );
};
